/**
 * The location reducer bodies, as plain functions on the draft shipment.
 *
 * Three rules live here. Setting a location recommends accessorials for its
 * type, but only when the type actually changed — otherwise an accessorial the
 * user had removed would come straight back on the next keystroke. Accessorials
 * the location type can't have are dropped every time, changed or not. And a
 * reset has to decide what the two locations start as, which depends on whether
 * the customer has an address book and whether this flow was given defaults.
 */

import {
  generateRecommendedAccessorialsForLocation,
  removeUnavailableAccessorialsForLocation,
} from "@/Shared/Locations/Helpers/recommendedAccessorialsForLocation";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { UUID } from "@/Shared/types";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import {
  bookShipmentEmptyLocationInfo,
  initialBookShipmentSliceState,
} from "./bookShipmentEmptyState";
import type { BookShipmentSlice2Type } from "@/Shared/Types/bookShipmentSliceTypes";

export function bookShipmentLocationWithRecommendedAccessorials(
  currentLocation: AddLocationLocationType,
  newLocation: AddLocationLocationType,
): AddLocationLocationType {
  // Only introduce recommended accessorials if the location type changed in this
  const locationTypeChanged =
    currentLocation.locationType !== newLocation.locationType;

  const withRecommended = locationTypeChanged
    ? generateRecommendedAccessorialsForLocation(
        newLocation.locationType,
        newLocation.accessorials,
      )
    : newLocation.accessorials;

  return {
    ...newLocation,
    // The removal runs every time, not just on a type change: a saved address
    // can arrive already residential with inside still ticked, which the
    // "changed" shortcut above would wave straight through.
    accessorials: removeUnavailableAccessorialsForLocation(
      newLocation.locationType,
      withRecommended,
    ),
  };
}

/**
 * A saved location straight out of the address book, cleaned of anything its
 * location type can't have. The reset path assigns the customer's default
 * location directly, so without this a residential default arrives with inside
 * still on it and no reducer ever looks at it again.
 */
function savedLocationForDraft(
  location: AddLocationLocationType,
): AddLocationLocationType {
  return {
    ...location,
    accessorials: removeUnavailableAccessorialsForLocation(
      location.locationType,
      location.accessorials,
    ),
  };
}

interface BookShipmentResetInput {
  savedLocations: Array<SavedLocation>;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
}

export function bookShipmentResetState(
  input: BookShipmentResetInput,
): BookShipmentSlice2Type {
  const newState = initialBookShipmentSliceState();

  const { savedLocations, defaultPickupLocationId, defaultDeliveryLocationId } =
    input;

  if (savedLocations.length === 0) {
    newState.enteringNewPickup = true;
    newState.selectedPickupSavedLocationId = undefined;
    newState.shipment.pickupLocation = bookShipmentEmptyLocationInfo();

    newState.enteringNewDelivery = true;
    newState.selectedDeliverySavedLocationId = undefined;
    newState.shipment.deliveryLocation = bookShipmentEmptyLocationInfo();
  }

  if (savedLocations.length > 0) {
    if (defaultPickupLocationId !== undefined) {
      newState.enteringNewPickup = false;
      newState.selectedPickupSavedLocationId = defaultPickupLocationId;

      const defaultLocation = savedLocations.find(
        (sl) => sl.savedLocationId === defaultPickupLocationId,
      );

      if (defaultLocation === undefined) {
        throw new Error("Missing pickup location");
      }

      newState.shipment.pickupLocation = savedLocationForDraft(
        defaultLocation.location,
      );
      newState.shipment.pickupLocationHasDock = defaultLocation.locationHasDock;
      newState.shipment.pickupLocationHasForklift =
        defaultLocation.locationHasForklift;
    } else {
      newState.enteringNewPickup = true;
      newState.selectedPickupSavedLocationId = undefined;
      newState.shipment.pickupLocation = bookShipmentEmptyLocationInfo();
    }

    if (defaultDeliveryLocationId !== undefined) {
      newState.enteringNewDelivery = false;
      newState.selectedDeliverySavedLocationId = defaultDeliveryLocationId;

      const defaultLocation = savedLocations.find(
        (sl) => sl.savedLocationId === defaultDeliveryLocationId,
      );

      if (defaultLocation === undefined) {
        throw new Error("Missing pickup location");
      }

      newState.shipment.deliveryLocation = savedLocationForDraft(
        defaultLocation.location,
      );
      newState.shipment.deliveryLocationHasDock =
        defaultLocation.locationHasDock;
      newState.shipment.deliveryLocationHasForklift =
        defaultLocation.locationHasForklift;
    } else {
      newState.enteringNewDelivery = true;
      newState.selectedDeliverySavedLocationId = undefined;
      newState.shipment.deliveryLocation = bookShipmentEmptyLocationInfo();
    }
  }

  return newState;
}
